import { AdditionalInfoStep } from "@/D2C/features/car-quote/components/post-quote/AdditionalInfoStep";
import { DocumentUploadStep } from "@/D2C/features/car-quote/components/post-quote/DocumentUploadStep";
import { SelectedCarQuoteSummary } from "@/D2C/features/car-quote/components/pre-quote/SelectedCarQuoteSummary";
import HealthAdditionalInfoStep from "@/D2C/features/health-quote/components/post-quote/AdditionalInfoStep";
import HealthDocumentUploadStep from "@/D2C/features/health-quote/components/post-quote/DocumentUploadStep";
import { LifeDocumentUploadStep } from "@/D2C/features/life-quote/components/post-quote/LifeDocumentUploadStep";
import TravelAdditionalInfoStep from "@/D2C/features/travel-quote/components/post-quote/AdditionalStep";
import TravelDocumentUploadStep from "@/D2C/features/travel-quote/components/post-quote/UploadDocumentStep";
import { LOB_IDS } from "@/shared/common/static";
import { Button } from "@/shared/components";
import { SelectedQuoteSummary } from "@/shared/components/SelectedQuoteSummary";
import { MoveLeft } from "lucide-react";
import { forwardRef } from "react";
import { AddQuoteProduct } from "../config/quoteProducts.config";
import type { useQuoteRefs } from "../hooks/useQuoteRefs";
import { B2CPostQuoteCompactStyles } from "./B2CPostQuoteCompactStyles";

type Refs = ReturnType<typeof useQuoteRefs>;

type Props = {
  product: AddQuoteProduct & { lobId: number };
  refs: Refs;
  selectedQuote: any;
  isPostSubmitting: boolean;
  travelUploadedDocuments: any;
  carUploadedDocuments: any;
  healthUploadedDocuments: any;
  travelStructuredOcrData: any;
  carStructuredOcrData: any;
  healthStructuredOcrData: any;
  onSetTravelUploads: (updater: (prev: any) => any) => void;
  onSetCarUploads: (updater: (prev: any) => any) => void;
  onSetHealthUploads: (updater: (prev: any) => any) => void;
  onBack: () => void;
  isPostDataReady: boolean;
  isSendingUploadLink: boolean;
  isSendingPaymentLink: boolean;
  isReadOnly?: boolean;
  travelAdditionalDefaults?: any;
  travelDocumentDefaults?: any;
  carAdditionalDefaults?: any;
  carDocumentDefaults?: any;
  healthAdditionalDefaults?: any;
  healthDocumentDefaults?: any;
  travelDetails?: any;
  onSavePostData: () => void;
  onSendUploadLink: () => void;
  onSendPaymentLink: () => void;
  onUploadConfirmChange?: (checked: boolean) => void;
  onAdditionalConfirmChange?: (checked: boolean) => void;
  showSaveButton?: boolean;
  showSectionTitles?: boolean;
  isUploadConfirmed?: boolean;
  isPaymentFailed?: boolean;
};

export const PostQuoteSection = forwardRef<HTMLDivElement, Props>(
  (
    {
      product,
      refs,
      selectedQuote,
      isPostSubmitting,
      travelUploadedDocuments,
      carUploadedDocuments,
      healthUploadedDocuments,
      travelStructuredOcrData,
      carStructuredOcrData,
      healthStructuredOcrData,
      onSetTravelUploads,
      onSetCarUploads,
      onSetHealthUploads,
      onBack,
      isPostDataReady,
      isSendingUploadLink,
      isSendingPaymentLink,
      isReadOnly = false,
      travelAdditionalDefaults,
      travelDocumentDefaults,
      carAdditionalDefaults,
      carDocumentDefaults,
      healthAdditionalDefaults,
      healthDocumentDefaults,
      travelDetails,
      onSavePostData,
      onSendUploadLink,
      onSendPaymentLink,
      onUploadConfirmChange,
      onAdditionalConfirmChange,
      showSaveButton = true,
      showSectionTitles = true,
      isUploadConfirmed = false,
      isPaymentFailed = false,
    },
    ref
  ) => {
    const resolvedTravelDetails =
      travelDetails ?? (refs.travelDetailsRef.current?.getValues() as any);

    const renderUploadAndInfo = () => {
      if (product.lobId === LOB_IDS.TRAVEL) {
        return (
          <>
            <div className="space-y-2 rounded-lg border border-theme-border bg-theme-surface p-3 xl:col-span-5">
              {showSectionTitles ? (
                <h4 className="text-sm font-semibold text-theme-text">Upload Documents</h4>
              ) : null}
              <TravelDocumentUploadStep
                isReadOnly={isReadOnly}
                isShowUploadGuidelines={false}
                showRequiredIdentificationTitle={showSectionTitles}
                ref={refs.travelDocumentUploadRef}
                defaultValues={travelDocumentDefaults}
                travelDetails={resolvedTravelDetails}
                onConfirmDocumentsChange={onUploadConfirmChange}
                persistedDocuments={travelUploadedDocuments}
                onUploadComplete={({ persistedDocument }) =>
                  onSetTravelUploads((prev) => ({
                    ...prev,
                    [persistedDocument.fieldKey]: persistedDocument,
                  }))
                }
              />
            </div>
            <div className="space-y-2 rounded-lg border border-theme-border bg-theme-surface p-3 xl:col-span-4">
              {showSectionTitles ? (
                <h4 className="text-sm font-semibold text-theme-text">Additional Information</h4>
              ) : null}
              <TravelAdditionalInfoStep
                ref={refs.travelAdditionalInfoRef}
                isReadOnly={isReadOnly}
                showPersonalInformationTitle={showSectionTitles}
                defaultValues={travelAdditionalDefaults}
                ocrStructuredData={travelStructuredOcrData}
                ocrDocuments={travelUploadedDocuments}
                travelDetails={resolvedTravelDetails}
                onResidentConfirmChange={onAdditionalConfirmChange}
              />
            </div>
          </>
        );
      }

      if (product.lobId === LOB_IDS.LIFE) {
        return (
          <>
            <div className="space-y-2 rounded-lg border border-theme-border bg-theme-surface p-3">
              {showSectionTitles ? (
                <h4 className="text-sm font-semibold text-theme-text">Upload Documents</h4>
              ) : null}
              <LifeDocumentUploadStep
                isReadOnly={isReadOnly}
                isShowUploadGuidelines={false}
                showRequiredIdentificationTitle={showSectionTitles}
                ref={refs.travelDocumentUploadRef}
                defaultValues={travelDocumentDefaults}
                // travelDetails={resolvedTravelDetails}
                onConfirmDocumentsChange={onUploadConfirmChange}
                persistedDocuments={travelUploadedDocuments}
                onUploadComplete={({ persistedDocument }) =>
                  onSetTravelUploads((prev) => ({
                    ...prev,
                    [persistedDocument.fieldKey]: persistedDocument,
                  }))
                }
              />
            </div>
            {/* <div className="space-y-2 rounded-lg border border-theme-border bg-theme-surface p-3">
              {showSectionTitles ? (
                <h4 className="text-sm font-semibold text-theme-text">Additional Information</h4>
              ) : null}
              <LifeAdditionalInfoStep
                ref={refs.travelAdditionalInfoRef}
                isReadOnly={isReadOnly}
                showPersonalInformationTitle={showSectionTitles}
                defaultValues={travelAdditionalDefaults}
                ocrStructuredData={travelStructuredOcrData}
                ocrDocuments={travelUploadedDocuments}
                travelDetails={resolvedTravelDetails}
                onResidentConfirmChange={onAdditionalConfirmChange}
              />
            </div> */}
          </>
        );
      }

      if (product.lobId === LOB_IDS.MOTOR) {
        return (
          <>
            <div className="space-y-2 rounded-lg border border-theme-border bg-theme-surface p-3">
              {showSectionTitles ? (
                <h4 className="text-sm font-semibold text-theme-text">Upload Documents</h4>
              ) : null}
              <DocumentUploadStep
                isReadOnly={isReadOnly}
                isShowUploadGuidelines={false}
                showTitle={showSectionTitles}
                ref={refs.carDocumentUploadRef}
                defaultValues={carDocumentDefaults}
                onConfirmChange={onUploadConfirmChange}
                persistedDocuments={carUploadedDocuments}
                onUploadComplete={({ persistedDocument }) =>
                  onSetCarUploads((prev) => ({
                    ...prev,
                    [persistedDocument.fieldKey]: persistedDocument,
                  }))
                }
              />
            </div>
            <div className="space-y-2 rounded-lg border border-theme-border bg-theme-surface p-3">
              {showSectionTitles ? (
                <h4 className="text-sm font-semibold text-theme-text">Additional Information</h4>
              ) : null}
              <AdditionalInfoStep
                ref={refs.carAdditionalInfoRef}
                isReadOnly={isReadOnly}
                defaultValues={carAdditionalDefaults}
                driverDateOfBirth={refs.carDriverRef.current?.getValues()?.dateOfBirth}
                ocrStructuredData={carStructuredOcrData}
                onAuthorizationConfirmChange={onAdditionalConfirmChange}
              />
            </div>
          </>
        );
      }

      return (
        <>
          <div className="space-y-2 rounded-lg border border-theme-border bg-theme-surface p-3">
            {showSectionTitles ? (
              <h4 className="text-sm font-semibold text-theme-text">Upload Documents</h4>
            ) : null}
            <HealthDocumentUploadStep
              isReadOnly={isReadOnly}
              isShowUploadGuidelines={false}
              showRequiredIdentificationTitle={showSectionTitles}
              ref={refs.healthDocumentUploadRef}
              defaultValues={healthDocumentDefaults}
              onConfirmDocumentsChange={onUploadConfirmChange}
              persistedDocuments={healthUploadedDocuments}
              onUploadComplete={({ persistedDocument }) =>
                onSetHealthUploads((prev) => ({
                  ...prev,
                  [persistedDocument.fieldKey]: persistedDocument,
                }))
              }
            />
          </div>
          <div className="space-y-2 rounded-lg border border-theme-border bg-theme-surface p-3">
            {showSectionTitles ? (
              <h4 className="text-sm font-semibold text-theme-text">Additional Information</h4>
            ) : null}
            <HealthAdditionalInfoStep
              ref={refs.healthAdditionalInfoRef}
              isReadOnly={isReadOnly}
              defaultValues={healthAdditionalDefaults}
              ocrStructuredData={healthStructuredOcrData}
              onResidentConfirmChange={onAdditionalConfirmChange}
            />
          </div>
        </>
      );
    };

    return (
      <div ref={ref} className="B2C-post-quote-compact">
        <B2CPostQuoteCompactStyles />
        <Button variant="secondary" onClick={onBack} className="mb-2" disabled={isReadOnly}>
          <MoveLeft className="mr-2 h-4 w-4" />
          Back to Quotes
        </Button>
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
          <div className="xl:col-span-3">
            {product.lobId === LOB_IDS.MOTOR ? (
              <SelectedCarQuoteSummary
                quote={selectedQuote}
                showCommission
                carValue={
                  selectedQuote?.sections?.[0]?.items?.find((i: any) => i.label === "Car Value")
                    ?.value
                }
              />
            ) : (
              <SelectedQuoteSummary quote={selectedQuote} showCommission />
            )}
          </div>
          {product.lobId === LOB_IDS.TRAVEL ? (
            renderUploadAndInfo()
          ) : (
            <fieldset disabled={isReadOnly} className="contents">
              <div className="grid grid-cols-1 gap-3 xl:col-span-9 xl:grid-cols-2">
                {renderUploadAndInfo()}
              </div>
            </fieldset>
          )}
        </div>

        <div className="mt-6">
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              className="rounded-lg min-w-[300px]"
              isLoading={isSendingUploadLink}
              loadingText="Sending document link..."
              disabled={isReadOnly || isUploadConfirmed}
              onClick={onSendUploadLink}
            >
              Send Upload Link to Client
            </Button>
            <Button
              className="rounded-lg min-w-[300px]"
              isLoading={isSendingPaymentLink}
              loadingText="Sending payment link..."
              disabled={!isPaymentFailed && (isReadOnly || !isPostDataReady)}
              onClick={onSendPaymentLink}
            >
              Send Payment Link to Client
            </Button>
            {showSaveButton ? (
              <Button
                className="rounded-lg"
                isLoading={isPostSubmitting}
                loadingText="Saving..."
                disabled={isReadOnly}
                onClick={onSavePostData}
              >
                Save Upload & Additional Details
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
);

PostQuoteSection.displayName = "PostQuoteSection";
